import React, { useState, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'sonner'
import { useAuth } from '../../../context/AuthContext'
import { shippingAddressSchema } from '../../../schemas/customerSchema'
import { useCart } from '../../../context/CartContext'
import { useCreateOrder } from '../../../hooks/queries/useOrders'
import { useProduct } from '../../../hooks/queries/useProducts'
import { useCategoryImageMap } from '../../../hooks/useCategoryImageMap'
import type { OrderFormData } from '../../../types'
import styled from 'styled-components'
import RadarAddressAutocomplete, { type RadarAddress } from '../../../components/RadarAddressAutocomplete'
import ProductFlyerModal from '@design-components/ProductFlyerModal'
import { AppModal } from '../../../components/AppModal'
import { getProductImage, handleImageError } from '../../../utils/productImages'
import {
  FormGroup as BaseFormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormCheckbox,
  FormRow,
  FieldError,
  CharCount,
  SectionTitle,
  GrayButton,
  PrimaryButton,
  colors,
  transitions
} from '../../../theme/sharedComponents'

interface PlaceOrderModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

// Override FormGroup for mobile-optimized spacing
const FormGroup = styled(BaseFormGroup)`
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    margin-bottom: 0.875rem;
  }
`

// Mobile-optimized form input with larger touch target
const MobileFormInput = styled(FormInput)`
  padding: 0.875rem 1rem;
  font-size: 1rem;
  min-height: 48px;

  @media (max-width: 576px) {
    font-size: 16px; /* Prevents zoom on iOS */
  }
`

// Mobile-optimized textarea
const MobileFormTextarea = styled(FormTextarea)`
  padding: 0.875rem 1rem;
  font-size: 1rem;
  min-height: 100px;

  @media (max-width: 576px) {
    font-size: 16px;
  }
`

const AutoFillSection = styled.div<{ $hasValue: boolean }>`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: var(--radius);
  border: 1px dashed ${({ $hasValue }) => ($hasValue ? '#28a745' : '#dee2e6')};
  background-color: ${({ $hasValue }) => ($hasValue ? 'rgba(40, 167, 69, 0.05)' : '#f8f9fa')};
  transition: all 0.2s ease;
`

const AutoFillLabel = styled.div<{ $hasValue: boolean }>`
  font-size: 0.75rem;
  color: ${({ $hasValue }) => ($hasValue ? '#28a745' : '#6c757d')};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  i {
    font-size: 0.8rem;
  }
`

// Order summary section
const OrderSummary = styled.div`
  background: ${colors.backgroundAlt};
  border-radius: var(--radius);
  padding: 0.6rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const OrderItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  background: white;
  border-radius: var(--radius);
  border: 1px solid ${colors.borderLight};
`

const OrderItemImageWrap = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  border-radius: var(--radius);
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 5%, transparent) 0%, ${colors.backgroundAlt} 100%);
  border: 1px solid ${colors.borderLight};
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 0.3rem;
  }
`

const OrderItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const OrderItemName = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 0.9rem;
  color: ${colors.textLightMode};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const OrderItemMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${colors.textLightModeMuted};

  code {
    background: white;
    padding: 0.1rem 0.3rem;
    border-radius: 0.2rem;
  }
`

const OrderItemPrice = styled.div`
  text-align: right;
  flex-shrink: 0;

  .qty {
    font-size: 0.8rem;
    color: ${colors.textLightModeMuted};
    margin: 0;
  }

  .total {
    font-weight: 600;
    color: ${colors.textLightMode};
    margin: 0;
  }
`

const OrderTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 2px solid ${colors.borderLight};

  .label {
    font-weight: 600;
    font-size: 1rem;
    color: ${colors.textLightMode};
  }

  .total {
    font-weight: 700;
    font-size: 1.25rem;
    color: ${colors.success};
  }
`

const AddressSection = styled.div`
  background: ${colors.backgroundAlt};
  padding: 1.25rem;
  border-radius: var(--radius);
  margin-bottom: 1.5rem;
  border: 1px solid ${colors.borderLight};
`

const StoredAddress = styled.div`
  background: white;
  padding: 1rem;
  border-radius: var(--radius);
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${colors.textLightMode};
  border: 1px solid ${colors.borderLight};

  .icon {
    color: ${colors.success};
    margin-right: 0.5rem;
  }
`

const AddressCheckbox = styled(FormCheckbox)`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: white;
  border-radius: var(--radius);
  border: 1px solid ${colors.borderLight};
  transition: ${transitions.fast};

  &:hover {
    border-color: ${colors.primary};
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
  }
`

const WarningBox = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #856404;
  padding: 0.875rem 1rem;
  border-radius: var(--radius);
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 1rem;
    flex-shrink: 0;
  }
`

const CreditSection = styled.div`
  background: ${colors.backgroundAlt};
  border-radius: var(--radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${colors.borderLight};
`

const CreditRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.9rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.borderLight};
  }

  .label {
    color: ${colors.textLightModeMuted};
  }

  .value {
    font-weight: 600;
    color: ${colors.textLightMode};
  }

  &.total {
    padding-top: 0.75rem;
    margin-top: 0.25rem;

    .label {
      font-weight: 600;
      color: ${colors.textLightMode};
    }

    .value {
      font-size: 1rem;
    }
  }

  &.remaining {
    .value {
      color: ${colors.success};
    }

    &.warning .value {
      color: ${colors.warning};
    }

    &.danger .value {
      color: ${colors.danger};
    }
  }
`

const CreditErrorBox = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #721c24;
  padding: 1rem;
  border-radius: var(--radius);
  margin-bottom: 1rem;
  font-size: 0.9rem;

  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;

    i {
      font-size: 1.1rem;
    }
  }

  .details {
    padding-left: 1.6rem;
    font-size: 0.85rem;
    color: #a94442;
  }
`

const UnlimitedBadge = styled.span`
  background: ${colors.success};
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
`


const PlaceOrderModal: React.FC<PlaceOrderModalProps> = ({ show, onClose, onSuccess }) => {
  const { user, credit, refreshCredit } = useAuth()
  const { items, getTotal } = useCart()
  const createOrder = useCreateOrder()

  // Shipping address - defaults to billing, can be different per order
  const [shipToBilling, setShipToBilling] = useState(true)
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
  })
  const [notes, setNotes] = useState('')
  const [poNumber, setPoNumber] = useState('')

  // Handle address selection from Radar autocomplete
  const handleShippingAddressSelect = (address: RadarAddress) => {
    const streetAddress = [address.number, address.street].filter(Boolean).join(' ')
    setShippingAddress((prev) => ({
      ...prev,
      street: streetAddress,
      city: address.city || '',
      state: address.stateCode || '',
      zip: address.postalCode || '',
    }))
    setFieldErrors({})
  }
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'street' | 'city' | 'state' | 'zip', string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const { data: flyerProductData } = useProduct(selectedProductId)
  const categoryImageMap = useCategoryImageMap()

  const total = getTotal()

  // Refresh credit when modal opens
  useEffect(() => {
    if (show) {
      refreshCredit()
    }
  }, [show, refreshCredit])

  // Credit calculations
  const isUnlimited = credit?.is_unlimited ?? true
  const availableCredit = credit?.available_credit ?? null
  const usedCredit = credit?.used_credit ?? 0
  const creditLimit = credit?.credit_limit ?? null
  const remainingAfterOrder = availableCredit !== null ? availableCredit - total : null
  const exceedsCredit = !isUnlimited && availableCredit !== null && total > availableCredit
  const shortfall = exceedsCredit && availableCredit !== null ? total - availableCredit : 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate custom shipping address if not shipping to billing
    if (!shipToBilling) {
      const result = shippingAddressSchema.safeParse(shippingAddress)
      if (!result.success) {
        const errs: typeof fieldErrors = {}
        for (const issue of result.error.issues) {
          const field = issue.path[0] as keyof typeof errs
          if (!errs[field]) errs[field] = issue.message
        }
        setFieldErrors(errs)
        return
      }
      setFieldErrors({})
    }

    setIsSubmitting(true)

    try {
      if (!user?.id) {
        throw new Error('User not found')
      }

      // Billing always comes from user profile
      // Shipping is either same as billing or custom entered
      const orderData: OrderFormData = {
        customer_id: user.id,
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.price,
        })),
        // Billing address from user profile
        billing_street: user.billing_street || undefined,
        billing_city: user.billing_city || undefined,
        billing_state: user.billing_state || undefined,
        billing_zip: user.billing_zip || undefined,
        // Shipping address - use billing or custom
        shipping_street: shipToBilling ? (user.billing_street || '') : shippingAddress.street,
        shipping_street2: shipToBilling ? (user.billing_street2 || '') : shippingAddress.street2,
        shipping_city: shipToBilling ? (user.billing_city || '') : shippingAddress.city,
        shipping_state: shipToBilling ? (user.billing_state || '') : shippingAddress.state,
        shipping_zip: shipToBilling ? (user.billing_zip || '') : shippingAddress.zip,
      }

      if (notes) {
        orderData.notes = notes
      }

      if (poNumber.trim()) {
        orderData.po_number = poNumber.trim()
      }

      await createOrder.mutateAsync(orderData)
      // Refresh credit after successful order
      await refreshCredit()
      toast.success('Order placed successfully!')
      onSuccess()
    } catch (err) {
      console.error('Failed to place order:', err)
      // Handle credit-specific error
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error_code?: string; error?: string; details?: { shortfall?: number } } } }
        if (axiosErr.response?.data?.error_code === 'INSUFFICIENT_CREDIT') {
          const details = axiosErr.response.data.details
          setError(`Insufficient credit. You need $${details?.shortfall?.toFixed(2)} more to place this order.`)
          refreshCredit()
          return
        }
        if (axiosErr.response?.data?.error_code === 'PO_NUMBER_REQUIRED') {
          setError('A PO number is required to place an order on this account.')
          return
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to place order')
    } finally {
      setIsSubmitting(false)
    }
  }

  const footer = (
    <>
      <GrayButton type="button" onClick={onClose} disabled={isSubmitting}>Cancel</GrayButton>
      <PrimaryButton
        type="submit"
        form="place-order-form"
        disabled={isSubmitting || items.length === 0 || exceedsCredit || (!!user?.require_po_number && !poNumber.trim())}
      >
        {isSubmitting ? (
          <>
            <Spinner animation="border" size="sm" />
            Placing Order...
          </>
        ) : exceedsCredit ? (
          <>
            <i className="bi bi-x-circle-fill"></i>
            Insufficient Credit
          </>
        ) : (
          <>
            <i className="bi bi-check-circle-fill"></i>
            Place Order · ${total.toFixed(2)}
          </>
        )}
      </PrimaryButton>
    </>
  )

  return (
    <>
    <AppModal
      show={show}
      onHide={onClose}
      size="lg"
      title="Review & Place Order"
      icon="bi bi-bag-check-fill"
      backdrop="static"
      error={error}
      footer={footer}
    >
        {/* Order Summary - Card-based for mobile */}
        <SectionTitle>
          <i className="bi bi-receipt"></i>
          Order Summary
        </SectionTitle>
        <OrderSummary>
          {items.map((item) => (
            <OrderItemCard key={item.product_id}>
              <OrderItemImageWrap onClick={() => setSelectedProductId(item.product_id)} title="View product details">
                <img
                  src={getProductImage(item.image_url)}
                  alt={item.short_description}
                  onError={handleImageError}
                />
              </OrderItemImageWrap>
              <OrderItemInfo>
                <OrderItemName title={item.short_description}>{item.short_description}</OrderItemName>
                <OrderItemMeta>
                  <code>{item.sku}</code> · ${item.price.toFixed(2)} ea
                </OrderItemMeta>
              </OrderItemInfo>
              <OrderItemPrice>
                <p className="qty">Qty: {item.quantity}</p>
                <p className="total">${(item.price * item.quantity).toFixed(2)}</p>
              </OrderItemPrice>
            </OrderItemCard>
          ))}
          <OrderTotalRow>
            <span className="label">Order Total</span>
            <span className="total">${total.toFixed(2)}</span>
          </OrderTotalRow>
        </OrderSummary>

        {/* Credit Status Section - Only show for customers with credit limits */}
        {!isUnlimited && (
          <>
            <SectionTitle>
              <i className="bi bi-wallet2"></i>
              Credit Status
            </SectionTitle>
            <CreditSection>
              <CreditRow>
                <span className="label">Credit Limit</span>
                <span className="value">${creditLimit?.toFixed(2)}</span>
              </CreditRow>
              <CreditRow>
                <span className="label">Used Credit</span>
                <span className="value">${usedCredit?.toFixed(2)}</span>
              </CreditRow>
              <CreditRow>
                <span className="label">Available Credit</span>
                <span className="value">${availableCredit?.toFixed(2)}</span>
              </CreditRow>
              <CreditRow className="total">
                <span className="label">This Order</span>
                <span className="value">-${total.toFixed(2)}</span>
              </CreditRow>
              <CreditRow
                className={`remaining ${
                  remainingAfterOrder !== null && remainingAfterOrder < 0
                    ? 'danger'
                    : remainingAfterOrder !== null && remainingAfterOrder < 100
                      ? 'warning'
                      : ''
                }`}
              >
                <span className="label">Remaining After Order</span>
                <span className="value">
                  ${remainingAfterOrder !== null ? remainingAfterOrder.toFixed(2) : '0.00'}
                </span>
              </CreditRow>
            </CreditSection>

            {exceedsCredit && (
              <CreditErrorBox>
                <div className="title">
                  <i className="bi bi-exclamation-octagon-fill"></i>
                  Insufficient Credit
                </div>
                <div className="details">
                  This order exceeds your available credit by ${shortfall.toFixed(2)}.
                  Please contact your account manager to increase your credit limit.
                </div>
              </CreditErrorBox>
            )}
          </>
        )}

        {isUnlimited && (
          <>
            <SectionTitle>
              <i className="bi bi-wallet2"></i>
              Credit Status
            </SectionTitle>
            <CreditSection>
              <CreditRow>
                <span className="label">Credit Limit</span>
                <UnlimitedBadge>Unlimited</UnlimitedBadge>
              </CreditRow>
            </CreditSection>
          </>
        )}

        <form id="place-order-form" onSubmit={handleSubmit}>
          {/* Billing Address (from profile) */}
          <SectionTitle>
            <i className="bi bi-credit-card-fill"></i>
            Billing Address
          </SectionTitle>
          <AddressSection>
            {user?.billing_street ? (
              <StoredAddress>
                <i className="bi bi-check-circle-fill icon"></i>
                {user.billing_street}<br />
                {user.billing_city}, {user.billing_state} {user.billing_zip}
              </StoredAddress>
            ) : (
              <WarningBox>
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>No billing address on file. Please contact support.</span>
              </WarningBox>
            )}
          </AddressSection>

          {/* Shipping Address */}
          <SectionTitle>
            <i className="bi bi-geo-alt-fill"></i>
            Shipping Address
          </SectionTitle>
          <AddressSection>
            <AddressCheckbox>
              <input
                type="checkbox"
                checked={shipToBilling}
                onChange={(e) => {
                  setShipToBilling(e.target.checked)
                  if (e.target.checked) setFieldErrors({})
                }}
              />
              <span>Ship to billing address</span>
            </AddressCheckbox>

            {!shipToBilling && (
              <>
                <FormGroup>
                  <FormLabel>Street Address</FormLabel>
                  <RadarAddressAutocomplete
                    onAddressSelect={handleShippingAddressSelect}
                    value={shippingAddress.street}
                    placeholder="Start typing an address..."
                    disabled={isSubmitting}
                  />
                  {fieldErrors.street && <FieldError>{fieldErrors.street}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Address Line 2</FormLabel>
                  <MobileFormInput
                    type="text"
                    value={shippingAddress.street2}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, street2: e.target.value })
                    }
                    placeholder="Apt, Suite, Unit, etc. (optional)"
                  />
                </FormGroup>

                <AutoFillSection $hasValue={!!shippingAddress.city}>
                  <AutoFillLabel $hasValue={!!shippingAddress.city}>
                    {shippingAddress.city ? (
                      <>
                        <i className="bi bi-check-circle-fill"></i>
                        Auto-filled from address
                      </>
                    ) : (
                      <>
                        <i className="bi bi-info-circle"></i>
                        These fields auto-fill when you select an address
                      </>
                    )}
                  </AutoFillLabel>
                  <FormRow>
                    <FormGroup style={{ flex: 2, marginBottom: 0 }}>
                      <FormLabel>City</FormLabel>
                      <MobileFormInput
                        type="text"
                        value={shippingAddress.city}
                        placeholder="City"
                        readOnly
                        style={{ backgroundColor: shippingAddress.city ? '#fff' : '#e9ecef' }}
                      />
                      {fieldErrors.city && <FieldError>{fieldErrors.city}</FieldError>}
                    </FormGroup>

                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                      <FormLabel>State</FormLabel>
                      <MobileFormInput
                        type="text"
                        value={shippingAddress.state}
                        placeholder="State"
                        readOnly
                        style={{ backgroundColor: shippingAddress.state ? '#fff' : '#e9ecef' }}
                      />
                      {fieldErrors.state && <FieldError>{fieldErrors.state}</FieldError>}
                    </FormGroup>

                    <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                      <FormLabel>ZIP Code</FormLabel>
                      <MobileFormInput
                        type="text"
                        value={shippingAddress.zip}
                        placeholder="ZIP"
                        readOnly
                        style={{ backgroundColor: shippingAddress.zip ? '#fff' : '#e9ecef' }}
                      />
                      {fieldErrors.zip && <FieldError>{fieldErrors.zip}</FieldError>}
                    </FormGroup>
                  </FormRow>
                </AutoFillSection>
              </>
            )}
          </AddressSection>

          {/* PO Number — always shown, required only when account requires it */}
          <>
            <SectionTitle>
              <i className="bi bi-file-earmark-text"></i>
              PO Number {user?.require_po_number && <span style={{ color: colors.danger, marginLeft: '0.2rem' }}>*</span>}
            </SectionTitle>
            <FormGroup>
              <MobileFormInput
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder={user?.require_po_number ? "Enter your purchase order number" : "Enter your purchase order number (optional)"}
                maxLength={100}
              />
            </FormGroup>
          </>

          {/* Notes */}
          <SectionTitle>
            <i className="bi bi-chat-left-text"></i>
            Order Notes
          </SectionTitle>
          <FormGroup>
            <MobileFormTextarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special instructions or notes for this order... (optional)"
              maxLength={500}
            />
            <CharCount $warning={notes.length > 400} $danger={notes.length > 475}>
              {notes.length}/500
            </CharCount>
          </FormGroup>
        </form>
    </AppModal>

    {flyerProductData?.product && (
      <ProductFlyerModal
        product={flyerProductData.product}
        onClose={() => setSelectedProductId(null)}
        isAuthenticated={!!user}
        isAdmin={user?.is_admin ?? false}
        categoryImageMap={categoryImageMap}
      />
    )}
    </>
  )
}

export default PlaceOrderModal
